// Docs tile (companion) — READ-ONLY consult: list · search · Preview · "Email to me". The document
// library lives on the shared Neon (docs_browse view, oid-scoped), re-served same-origin
// (/api/docs GET, rscan_sid cookie). Uploading/organising stays desktop-only. The R2 client +
// Resend live on the Outlook backend, so both ways of opening a file proxy there: Preview
// (/api/docs/file → presigned inline GET) and mail-to-me (/api/docs/mail → /api/docs-mail).
// Row chassis = the shared .rc-row.
import { toast } from "./app";
import { loadSnap, saveSnap, skeletonHtml } from "./cache";
import { askConnect } from "./connect";

const $ = <T extends Element = HTMLElement>(sel: string): T | null => document.querySelector<T>(sel);

type Doc = {
  id: number | string; // Neon returns BIGINT as a string
  file_name: string | null;
  summary: string | null;
  status: string | null; // pending | ready | …
  kind: string | null;
  is_image: boolean | null;
  size_bytes: number | string | null;
  remark: string | null;
  tags: string[] | null;
  first_seen_at: string | null;
  org_id?: number | null;        // ORG-DATA-OWNERSHIP: set = org-owned ("Company")
  author_name?: string | null;   // display name of the uploader (org docs only)
};
type Company = { enabled: boolean; orgName: string | null };

let all: Doc[] = [];
let company: Company = { enabled: false, orgName: null };
let total: number | null = null; // TRUE count from the server — the list is its first page (≤50)
let loaded = false;
const mailing = new Set<string>(); // in-flight sends → guard against a double tap
const previewing = new Set<string>(); // in-flight presigns → guard against a double tap

const esc = (s: unknown): string =>
  String(s ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]!));
const snippet = (s: string, n = 120): string => { const t = s.replace(/\s+/g, " ").trim(); return t.length > n ? t.slice(0, n - 1) + "…" : t; };
const fileExt = (n?: string | null): string => (/\.([a-z0-9]{1,5})$/i.exec(n || "")?.[1] || "").toUpperCase();
const relTime = (iso?: string | null): string => {
  if (!iso) return "";
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 60) return "just now";
  if (d < 3600) return `${Math.floor(d / 60)}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  if (d < 7 * 86400) return `${Math.floor(d / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
};

// Stale-while-revalidate: paint the last-known list (or a skeleton on a true first run)
// immediately, then fetch and re-render. The screen is never blanked while loading.
export async function loadDocs(force = false): Promise<void> {
  if (loaded && !force) { renderList(); return; }
  let painted = loaded;
  if (!loaded) {
    const snap = loadSnap<Doc[]>("docs");
    const snapCo = loadSnap<Company>("docs-company");
    if (snapCo) company = snapCo;
    if (snap) { all = snap; total = loadSnap<number>("docs-total"); painted = true; renderList(); }
    else { const list = $("#docsList"); if (list) list.innerHTML = skeletonHtml(); }
  }
  try {
    const term = ($("#docsSearch") as HTMLInputElement | null)?.value.trim() || "";
    const r = await fetch(`/api/docs${term ? `?q=${encodeURIComponent(term)}` : ""}`, { credentials: "include" });
    if (r.status === 401) { renderEmpty("This phone isn't linked. Reconnect from Outlook to load your documents."); return; }
    if (!r.ok) throw new Error(String(r.status));
    const j = (await r.json()) as { items?: Doc[]; total?: number; company?: Company };
    all = j.items || [];
    if (j.company) { company = j.company; saveSnap("docs-company", company); }
    total = typeof j.total === "number" ? j.total : null;
    loaded = true;
    saveSnap("docs", all);
    if (total != null) saveSnap("docs-total", total);
    renderList();
  } catch {
    if (!painted) renderEmpty("Couldn't load your documents. Check your connection and try again.");
  }
}

function renderEmpty(msg: string): void {
  const list = $("#docsList"); const empty = $("#docsEmpty");
  if (list) list.innerHTML = "";
  if (empty) { empty.textContent = msg; empty.style.display = "block"; }
  const count = $("#topCount"); if (count) { count.textContent = ""; count.hidden = true; }
}

// ── list (flat; search is server-side so it matches summary/remark/tags too) ──
function renderList(): void {
  const count = $("#topCount");
  const n = total ?? all.length; // true total when known — the loaded list is only the first page
  if (count && $("#screen-docs")?.classList.contains("active")) { count.textContent = n ? String(n) : ""; count.hidden = !n; }

  const empty = $("#docsEmpty"); const list = $("#docsList");
  if (!list) return;
  const term = ($("#docsSearch") as HTMLInputElement | null)?.value.trim();
  if (!all.length) {
    list.innerHTML = "";
    if (empty) { empty.textContent = term ? "No document matches your search." : "No documents yet — upload them on the desktop app."; empty.style.display = "block"; }
    return;
  }
  if (empty) empty.style.display = "none";

  const docIco = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/><path d="M8 13h8M8 17h6"/></svg>`;
  list.innerHTML = all
    .map((d) => {
      const sub = d.summary ? snippet(d.summary) : [fileExt(d.file_name), relTime(d.first_seen_at)].filter(Boolean).join(" · ");
      const ext = fileExt(d.file_name);
      // Tapping a row OPENS the document detail (summary + type + Email-to-me) — it no longer mails
      // straight away. The right slot shows the file type as a light badge.
      return `<button class="rc-row" data-id="${esc(d.id)}" title="Open document">` +
        `<span class="rc-rowic">${docIco}</span>` +
        `<span class="rc-rowbody"><span class="rc-rowtitle">${esc(d.file_name || "Document")}${d.org_id != null ? ` <span class="vf-chip-company">${esc(company.orgName || "Company")}</span>` : ""}</span><span class="rc-rowsub">${esc(sub)}</span></span>` +
        (ext ? `<span class="rc-rowamt" style="font-size:11px;font-weight:700;color:var(--text-mid)">${esc(ext)}</span>` : "") +
        `</button>`;
    })
    .join("");
}

// ── read-only detail (tap a document) — large summary + type + "Email to me" ──
let detailDoc: Doc | null = null;
function openDetail(d: Doc): void {
  detailDoc = d;
  const name = $("#docsDetailName"); if (name) name.textContent = d.file_name || "Document";
  const meta = $("#docsDetailMeta");
  if (meta) meta.textContent = [
    fileExt(d.file_name) || (d.is_image ? "Image" : "File"),
    relTime(d.first_seen_at),
    d.org_id != null ? (company.orgName || "Company") : "",
    d.org_id != null && d.author_name ? `added by ${d.author_name}` : "",
  ].filter(Boolean).join(" · ");
  const sum = $("#docsDetailSummary");
  if (sum) sum.textContent = d.summary ? d.summary : (d.status && d.status !== "ready" ? "RPlay is still reading this document…" : "No summary yet.");
  $("#docsDetail")?.classList.add("open");
}
function closeDetail(): void { $("#docsDetail")?.classList.remove("open"); detailDoc = null; }

// "Preview": open the stored file itself through a short-lived presigned URL (inline, so Safari
// renders a PDF or image in place instead of downloading it). Mailing it to yourself was the only
// way to SEE a document on the phone (Gery 2026-08-20). Same shape as the receipts "view original":
// the tab must be opened INSIDE the click gesture, or Safari blocks it as a popup.
async function previewDoc(d: Doc): Promise<void> {
  const id = String(d.id);
  if (previewing.has(id)) return;
  previewing.add(id);
  const w = window.open("", "_blank");
  try {
    const r = await fetch("/api/docs/file", {
      method: "POST", headers: { "content-type": "application/json" }, credentials: "include",
      body: JSON.stringify({ id: Number(d.id) }),
    });
    const j = (await r.json().catch(() => ({}))) as { url?: string; error?: string };
    if (r.ok && j.url) { if (w) w.location.href = j.url; else location.href = j.url; return; }
    w?.close();
    if (j.error === "not_found") toast("That file isn't available.");
    else toast("Couldn't open — try again");
  } catch { w?.close(); toast("Couldn't open — check your connection"); }
  finally { previewing.delete(id); }
}

// "Email to me": the Outlook backend reads the file from R2 and Resend mails it to the paired
// user's OWN address (owner-only, recipient never client-supplied). The phone just triggers it.
async function mailDoc(d: Doc): Promise<void> {
  const id = String(d.id);
  if (mailing.has(id)) return;
  mailing.add(id);
  toast("Emailing to you…");
  try {
    const r = await fetch("/api/docs/mail", {
      method: "POST", headers: { "content-type": "application/json" }, credentials: "include",
      body: JSON.stringify({ id: Number(d.id) }),
    });
    const j = (await r.json().catch(() => ({}))) as { ok?: boolean; error?: string };
    if (r.ok && j.error == null) toast(`“${d.file_name || "Document"}” emailed to you`);
    else if (j.error === "file_unavailable" || j.error === "not_found") toast("That file isn't available.");
    else if (j.error === "no_recipient") toast("No email on file — set one up on desktop.");
    else toast("Couldn't send — try again");
  } catch { toast("Couldn't send — check your connection"); }
  finally { mailing.delete(id); }
}

/** Home-search result tap → open the doc detail (after loadDocs' snapshot paint). */
export function openDocById(id: string): void {
  const d = all.find((x) => String(x.id) === id);
  if (d) openDetail(d);
}

// ── wiring ──
// SAVE-CANON D1: the phone can upload a doc (previously consult-only; "upload on the desktop").
// Base64 → /api/docs/upload (server-side sha256 + presign + dedup + register). 4.5MB cap — the
// sync-function payload budget; bigger files stay a desktop job (clear error, never silent).
async function uploadDoc(file: File): Promise<void> {
  if (file.size > 4_500_000) { toast("Too large for the phone (max ~4 MB) — upload it on the desktop"); return; }
  toast("Uploading…");
  try {
    const b64 = await new Promise<string>((res, rej) => {
      const r = new FileReader();
      r.onload = () => res(String(r.result).split(",")[1] || "");
      r.onerror = () => rej(r.error);
      r.readAsDataURL(file);
    });
    const r = await fetch("/api/docs/upload", {
      method: "POST", headers: { "content-type": "application/json" }, credentials: "include",
      body: JSON.stringify({ file_b64: b64, name: file.name, content_type: file.type || "application/octet-stream" }),
    });
    const j = (await r.json().catch(() => ({}))) as { ok?: boolean; inserted?: boolean; error?: string };
    if (r.status === 402) { toast("Docs are part of the subscription"); return; }
    if (r.status === 507 || j.error === "storage_full") { toast("Your 10 GB of document storage is full — remove some files"); return; }
    if (!r.ok) { toast("Upload failed — try again"); return; }
    toast(j.inserted === false ? "Already in your Docs (same file)" : "Uploaded — indexing…");
    void loadDocs(true);
  } catch { toast("Upload failed — check your connection"); }
}

export function initDocs(): void {
  $("#docsUploadBtn")?.addEventListener("click", () => ($("#docsFileInput") as HTMLInputElement | null)?.click());
  $("#docsFileInput")?.addEventListener("change", (e) => {
    const input = e.target as HTMLInputElement;
    const f = input.files?.[0];
    input.value = "";
    if (f) void uploadDoc(f);
  });
  const search = $("#docsSearch") as HTMLInputElement | null;
  let t: ReturnType<typeof setTimeout> | null = null;
  const onSearch = (): void => {
    const cl = $("#docsClear"); if (cl) cl.hidden = !search?.value;
    // server-side search (matches summary/remark/tags) — debounced
    if (t) clearTimeout(t);
    t = setTimeout(() => void loadDocs(true), 280);
  };
  search?.addEventListener("input", onSearch);
  $("#docsClear")?.addEventListener("click", () => { if (search) search.value = ""; onSearch(); search?.focus(); });
  $("#docsList")?.addEventListener("click", (e) => {
    const row = (e.target as HTMLElement).closest<HTMLElement>(".rc-row");
    if (row) { const d = all.find((x) => String(x.id) === (row.getAttribute("data-id") || "")); if (d) openDetail(d); }
  });
  $("#docsDetailBack")?.addEventListener("click", closeDetail);
  // "Chat with this document" → open the Brain scoped to Docs, seeded so RPlay retrieves THIS file and
  // gives an overview. Mirrors the desktop pane/PWA "Chat" action (Gery 2026-07-07). Chatting was
  // previously desktop-only; the companion Brain has a Docs mode, so we route through it by name.
  $("#docsChatBtn")?.addEventListener("click", () => {
    if (!detailDoc) return;
    const name = detailDoc.file_name || "this document";
    closeDetail();
    askConnect(`Give me a brief overview of my document “${name}”, then I'll ask follow-up questions.`, "docs");
  });
  $("#docsPreviewBtn")?.addEventListener("click", () => { if (detailDoc) void previewDoc(detailDoc); });
  $("#docsMailBtn")?.addEventListener("click", async () => {
    if (!detailDoc) return;
    const btn = $("#docsMailBtn") as HTMLButtonElement | null;
    if (btn) btn.disabled = true;
    await mailDoc(detailDoc);
    if (btn) btn.disabled = false;
  });
}
